import { IconButton } from 'components/atoms/IconButton';
import {
  CloseIcon,
  ErrorIconRound,
  InfoIcon,
  SuccessIcon,
  WarningIcon,
} from 'components/atoms/Icons';
import { Stack } from 'components/atoms/Stack';
import { cn } from 'utils/cn';
import Text from '../Text';
import type { ToastProps, ToastType } from './types';

export const TOAST_DURATION = 5000; // In milliseconds

const ToastMessage = ({
  title,
  description,
  actionButton,
}: Pick<ToastProps, 'title' | 'description' | 'actionButton'>) => {
  return (
    <div className="flex flex-1 flex-col gap-xs1">
      <Text data-test-id="toast-title" font="body" variant="headerRegular">
        {title}
      </Text>
      {description && (
        <Text data-test-id="toast-description" font="body" variant="body">
          {description}
        </Text>
      )}
      {actionButton && <Stack className="mt-xs3">{actionButton}</Stack>}
    </div>
  );
};

const getIcon = (type: ToastType) => {
  switch (type) {
    case 'info':
      return <InfoIcon color="iconInfo" customFill={true} />;
    case 'success':
      return <SuccessIcon color="iconSuccess" customFill={true} />;
    case 'error':
      return <ErrorIconRound color="iconDanger" customFill={true} />;
    case 'warning':
      return <WarningIcon color="iconWarning" customFill={true} />;
    default:
      return null;
  }
};

export const Toast = ({
  type,
  title,
  description,
  actionButton,
  onClose,
  dismissable = true,
  closeButtonProps,
}: ToastProps) => (
  <div
    className={cn(
      'flex gap-xs3 items-start w-[350px] h-auto',
      'py-sm1 px-sm2',
      'text-primary-inverse whitespace-pre-line',
      'bg-background-inverse rounded-medium',
    )}
    data-test-id="alert-message"
    data-type={type}
  >
    <div className="flex items-center">{getIcon(type)}</div>
    <div className="flex grow items-start [&_p]:w-full [&_p]:break-words">
      <ToastMessage
        actionButton={actionButton}
        description={description}
        title={title}
      />
      {dismissable && (
        <IconButton
          data-test-id="alert-window-close-button"
          onClick={onClose}
          {...closeButtonProps}
        >
          <CloseIcon />
        </IconButton>
      )}
    </div>
  </div>
);
